import { createRef, useEffect, useMemo, useState } from 'react';
import Form from '@rjsf/core';
import { FormValidation, RJSFSchema } from '@rjsf/utils';
import { CustomFieldDefinition, InventoryAsset, OutsideInventorySchema, Product } from '../../../types';
import { recordCustomFieldEditable } from '../../../utils/customFieldEditability';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { JSONSchemaForm } from '../../Compounds/JSONSchemaForm';
import { outsideInventoryWidgets } from '../../Compounds/OutsideInventoryForm/outsideInventoryWidgets';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import {
  getOutsideinventoryFormSubject,
  getOutsideInventoryFormValidSubject,
  initUpdateForm,
  setFormValidity,
  submitUpdateForm,
  updateForm,
  updateFormProduct
} from '../../../store/OutsideInventory';
import { TextSpan } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE, INPUT_STYLE } from '../../../utils/styleHelpers';
import { getOutsideInventorySchemaSubject } from '../../../store/OutsideInventorySchema';
import { getProductSubject } from '../../../store/Product';
import { DetailRow } from '../../Compounds/DetailsDrawer/DetailRow';
import { CustomFieldDetailRow } from '../../Compounds/DetailsDrawer/CustomFieldDetailRow';
import {
  shouldSeeComments,
  getEffectiveAccountId,
  getUser,
  isCapabilityDenied,
  isInternalUser
} from '../../../store/User';
import { useInventory, getInventorySubject, publishInventoryAsset } from '../../../store/Inventory';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { inventoryRowAccountId } from './inventoryGridColumnDefs';
import { isValidForEdit, outsideInventoryEditSchema } from './outsideInventoryEditSchema';
import { extractApiErrorMessage } from '../../Compounds/McpAccess/extractApiErrorMessage';
import { FAILED_SAVE } from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { ProductSelectionForm } from '../../Compounds/ProductSelectionForm';
import { validateSpeedUnits } from '../../Compounds/OutsideInventoryForm/OutsideInventoryForm';
import { Permission } from '../../../store/GeneralStore';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { FormStateStatus } from '../../../store/BulkForm';
import { clearForm } from '../../../store/AddPortalUsersToTeamFormState';
import { S3AttachmentTable } from '../../Compounds/AttachmentTable/S3AttachmentTable';
import { DetailsComments } from '../../Compounds/DetailsComments/DetailsComments';
import { DetailsLineItems } from '../../Compounds/DetailsLineItems/DetailsLineItems';
import { CaseDetailsDisplay } from '../SupportCases/CaseDetailsDisplay';
import { Autocomplete, TextField } from '@mui/material';
import { ReactNode } from 'react';
import { useRecordCustomFieldDefs } from '../../../Api/Admin/CustomFields/useRecordCustomFieldDefs';
import { upsertCustomFieldValuesApi } from '../../../Api/Admin/CustomFields/customFieldValuesApi';
import { refreshPriceBenchmarkForAsset } from '../../../store/PriceBenchMark';
import { productLabel } from '../../../utils/productLabel';

export interface OutsideInventoryDetailsProps {
  asset: InventoryAsset;
  extraSection?: ReactNode;
}

export const OutsideInventoryDetails = ({ asset, extraSection }: OutsideInventoryDetailsProps) => {
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [allFormsValid, setAllFormsValid] = useState(true);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [supportTypeSearchQuery, setSupportTypeSearchQuery] = useState(
    asset.upstack_support_managed ? 'Supported' : 'Not Supported'
  );
  const [product, setProduct] = useState<Product | null>();
  const [supportType, setSupportType] = useState<boolean>(asset.upstack_support_managed || false);
  const [allSchemas, setAllSchemas] = useState<OutsideInventorySchema[]>([]);
  const [schema, setSchema] = useState<OutsideInventorySchema>();
  const [readOnly, setReadOnly] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [formData, setFormData] = useState<InventoryAsset>({ ...asset });
  const [formId, setFormId] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formRef = createRef<Form<any, RJSFSchema, any>>();
  const { fetchInventory } = useInventory();
  const { setSnack } = useSnackbar();

  const customFieldDefs = useRecordCustomFieldDefs(
    inventoryRowAccountId(asset) || getEffectiveAccountId(),
    asset.custom_fields
  );
  const customFieldUnrestricted = isInternalUser(getUser());
  const customFieldDenied = isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser());
  const customFieldLocked = (def: CustomFieldDefinition) =>
    !recordCustomFieldEditable(asset.custom_fields, def, customFieldUnrestricted, customFieldDenied);

  const handleCustomFieldSave = async (definitionId: string, value: string) => {
    if (!asset.id) return;
    const { error } = await upsertCustomFieldValuesApi(asset.id, 'Inventory', [
      { custom_field_definition_id: definitionId, value }
    ]);
    if (error) {
      setSnack({ message: extractApiErrorMessage(error, FAILED_SAVE), open: true, type: 'error' });
      throw new Error(error.message);
    }
    const def = customFieldDefs.find((d) => d.id === definitionId);
    const updatedAsset: InventoryAsset = {
      ...asset,
      custom_fields: (asset.custom_fields ?? []).some((cf) => cf.definition_id === definitionId)
        ? (asset.custom_fields ?? []).map((cf) => (cf.definition_id === definitionId ? { ...cf, value } : cf))
        : [
            ...(asset.custom_fields ?? []),
            {
              definition_id: definitionId,
              name: def?.name ?? '',
              field_type: def?.field_type ?? 'text',
              position: def?.position ?? 0,
              value,
              is_editable: def?.is_editable,
              inherited: false
            }
          ]
    };
    publishInventoryAsset(updatedAsset);
  };

  // eslint-disable-next-line max-len
  const supportCaseMessage = `Go to ${asset?.supplier?.name} for Support. This service is managed outside of UPSTACK, this record is here for informational and budgetary reasons.`;

  useEffect(() => {
    const productSub = getProductSubject().subscribe((products) => setAllProducts(products));
    const schemasSub = getOutsideInventorySchemaSubject().subscribe((schemas) => setAllSchemas(schemas));
    const formSub = getOutsideinventoryFormSubject().subscribe((forms) => {
      setFormId(forms[0].id);
      setFormData(forms[0].formData);
    });
    const formsValidSub = getOutsideInventoryFormValidSubject().subscribe((valid) => setAllFormsValid(valid));

    initUpdateForm(formData);

    return () => {
      if (productSub) productSub.unsubscribe();
      if (schemasSub) schemasSub.unsubscribe();
      if (formSub) formSub.unsubscribe();
      if (formsValidSub) formsValidSub.unsubscribe();
      clearForm();
    };
  }, []);

  useEffect(() => {
    if (asset.id !== formData.id) {
      initUpdateForm(asset);
      setProduct(findProduct(asset.product_id || ''));
      setSupportType(asset.upstack_support_managed || false);
    }
  }, [asset.id]);

  useEffect(() => {
    if (allSchemas.length) {
      // Find the exact schema
      let matchedSchema = findSchema(formData.outside_inventory_schema_id || '');
      // If the product exists but the schema doesn't match, update it
      if (product && (!matchedSchema || product.id !== formData.product_id)) {
        matchedSchema = updateFormProduct(formId, product, allSchemas, '');
        const formValid = formRef.current?.validateForm() ?? allFormsValid;
        updateForm(formId, formData, false, formValid);
      }
      if (matchedSchema) {
        setSchema(matchedSchema);
      }
    }
  }, [allSchemas, product]);

  useEffect(() => {
    if (allProducts.length) {
      const matchedProduct = findProduct(formData.product_id || '');
      if (matchedProduct) {
        setProduct(matchedProduct);
        setProductSearchQuery(`${matchedProduct?.family} - ${matchedProduct?.name}`);
      }
    }
  }, [allProducts]);

  const editSchema = useMemo(
    () => schema && outsideInventoryEditSchema(schema.schema, allSchemas, { saved: asset, showAll, readOnly }),
    [schema, allSchemas, asset, showAll, readOnly]
  );

  useEffect(() => {
    if (!readOnly && editSchema) setFormValidity(formId, isValidForEdit(editSchema.validation_schema, formData));
  }, [editSchema]);

  const findSchema = (id: string) => allSchemas.find((schema) => schema.id === id);
  const findProduct = (id: string) => allProducts.find((prod) => prod.id === id);

  const handleChange = (form: InventoryAsset | null) => {
    const update: InventoryAsset = { ...formData, ...form };
    const formValid = editSchema ? isValidForEdit(editSchema.validation_schema, update) : allFormsValid;
    updateForm(formId, update, true, formValid);
  };

  const customValidate = (data: InventoryAsset, errors: FormValidation): FormValidation =>
    validateSpeedUnits(data as unknown as Record<string, unknown>, errors);

  const handleCancelForm = () => {
    setReadOnly(true);
    setProduct(findProduct(asset.product_id || ''));
    setSupportType(asset.upstack_support_managed || false);
    updateForm(formId, { ...asset }, false, true);
  };

  const handleInputChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setProductSearchQuery(value || '');
  };

  const handleSupportTypeInputChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setSupportTypeSearchQuery(value || '');
  };

  const handleProductSelectionChange = (_e: React.SyntheticEvent<Element, Event>, value: Product | null) => {
    setProduct(value);
  };

  const handleSupportTypeSelectionChange = (_e: React.SyntheticEvent<Element, Event>, value: boolean | null) => {
    setSupportType(value || false);
  };

  const onUpdate = async () => {
    setInventoryLoading(true);
    const result = await submitUpdateForm(supportType);
    if (result.find((res) => res.id === formId)?.status === FormStateStatus.SUCCESS) {
      setSnack({ message: 'Inventory updated successfully', type: 'success', open: true });
      await fetchInventory(true);
      const updatedAsset = getInventorySubject()
        .getValue()
        .find((a) => a.id === asset.id);
      if (updatedAsset) {
        setFormData({ ...updatedAsset });
        initUpdateForm(updatedAsset);
        publishInventoryAsset(updatedAsset);
      }
      refreshPriceBenchmarkForAsset(asset.id);
    } else {
      setSnack({ message: 'Inventory not updated', type: 'error', open: true });
    }
    setReadOnly(true);
    setInventoryLoading(false);
  };

  const toggleEditing = () => {
    if (readOnly) setShowAll(false);
    setReadOnly(!readOnly);
  };

  const generateCardActions = () => (
    <PermissibleRender requiredPermissions={[Permission.EDIT_OUTSIDE_INVENTORY]}>
      <div className="flex space-x-2 items-center">
        <button
          onClick={toggleEditing}
          className="flex space-x-2 items-center">
          <TextSpan
            color="indigo"
            size="sm14">
            Edit
          </TextSpan>
        </button>
      </div>
    </PermissibleRender>
  );

  return (
    <>
      <DetailViewCard
        collapsed={false}
        enableCollapse
        key="outside-inventory"
        cardActions={generateCardActions()}
        title="Details">
        <div className="overflow-auto max-h-[55vh] min-h-[30vh]">
          <>
            {inventoryLoading && (
              <div className="mt-32">
                <ComponentSpinner />
              </div>
            )}
            {!inventoryLoading && editSchema !== undefined && (
              <>
                {readOnly && (
                  <div className="w-full pr-4 mb-6">
                    <DetailRow
                      key="source"
                      colId="upstack_support_managed"
                      title="Support Level"
                      value={asset.upstack_support_managed ? 'Supported' : 'Not Supported'}
                      gutterBottom={false}
                    />
                  </div>
                )}
                {readOnly && product !== undefined && (
                  <div className="w-full pr-4">
                    <DetailRow
                      key={product?.id}
                      colId={product?.id || 'product'}
                      title="Product"
                      value={productLabel(product, { abbreviated: false })}
                      gutterBottom={false}
                    />
                  </div>
                )}
                {customFieldDefs.length > 0 && (
                  <div className="w-full pr-4 mt-2">
                    {customFieldDefs.map((def) => (
                      <CustomFieldDetailRow
                        key={def.id}
                        definition={def}
                        value={asset.custom_fields?.find((cf) => cf.definition_id === def.id)?.value ?? ''}
                        onSave={handleCustomFieldSave}
                        readOnly={customFieldLocked(def)}
                      />
                    ))}
                  </div>
                )}
                {!readOnly && (
                  <PermissibleRender requiredPermissions={[Permission.VIEW_ADMIN]}>
                    <div className="mt-2 mb-8 w-full">
                      <Autocomplete
                        value={supportType}
                        onInputChange={handleSupportTypeInputChange}
                        inputValue={supportTypeSearchQuery}
                        onChange={handleSupportTypeSelectionChange}
                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                        options={[false, true]}
                        getOptionLabel={(option: boolean) => (option ? 'Supported' : 'Not Supported')}
                        size="medium"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Support Level"
                            InputProps={{ ...params.InputProps, style: { fontSize: '.875rem' }, name: 'Support Level' }}
                            className={INPUT_STYLE}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            className="text-sm14 py-1 pl-4 cursor-pointer hover:bg-grey-1">
                            {option ? 'Supported' : 'Not Supported'}
                          </li>
                        )}
                      />
                    </div>
                  </PermissibleRender>
                )}
                {!readOnly && product !== undefined && (
                  <ProductSelectionForm
                    product={product}
                    allProducts={allProducts}
                    productSearchQuery={productSearchQuery}
                    handleInputChange={handleInputChange}
                    handleProductSelectionChange={handleProductSelectionChange}
                    classNames="mt-2 mb-4 w-full"
                  />
                )}
                <JSONSchemaForm
                  formKey={formData.id}
                  schema={editSchema.validation_schema}
                  uiSchema={editSchema.ui_schema}
                  formData={formData}
                  onChangeHandler={handleChange}
                  rjsfProps={{
                    widgets: outsideInventoryWidgets,
                    idPrefix: 'outside_inventory_update_form',
                    idSeparator: `_${formId}_`,
                    showErrorList: false,
                    readonly: readOnly,
                    liveValidate: !readOnly,
                    customValidate: customValidate,
                    ref: formRef,
                    formContext: {
                      styleOverride: { width: 'full' },
                      update: true,
                      hideEmpty: true,
                      showAll,
                      toggleShowAll: () => setShowAll(!showAll)
                    }
                  }}
                />
              </>
            )}
          </>
        </div>
      </DetailViewCard>
      {!readOnly && (
        <div className="flex flex-row justify-end mt-4">
          <button
            className={`${DEFAULT_CANCEL_BUTTON_STYLE} mr-2`}
            onClick={handleCancelForm}>
            Cancel
          </button>
          <button
            disabled={!allFormsValid}
            className={`${DEFAULT_ADVANCE_BUTTON_STYLE} ml-2`}
            onClick={() => onUpdate()}>
            Save
          </button>
        </div>
      )}
      {extraSection}
      {formData?.id && (
        <DetailsLineItems
          id={formData.id}
          mrc={formData.mRC}
          entryMethod={formData.entry_method}
        />
      )}
      <PermissibleRender requiredPermissions={[Permission.VIEW_CASES_ON_INVENTORY]}>
        <CaseDetailsDisplay
          data={asset}
          disabled={!formData.upstack_support_managed}
          disabledMessage={supportCaseMessage}
        />
      </PermissibleRender>
      {formData?.id && (
        <S3AttachmentTable
          id={formData.id}
          entityType="OutsideInventory"
        />
      )}
      {shouldSeeComments() && formData?.id && (
        <DetailsComments
          id={formData.id}
          entityType="OutsideInventory"
          entityAccountId={formData.account_id || ''}
        />
      )}
    </>
  );
};
